import { AdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import { IconInfo24 } from '@warp-ds/icons/react';
import { Box } from '@warp-ds/react/components/box';
import { Pagination } from '@warp-ds/react/components/pagination';
import { type ReactNode, useMemo } from 'react';
import { ApiActionStatus, PRICE_MIGHT_VARY_WARNING } from '@/interfaces';
import { EmptyWarning } from '../emptyWarning/EmptyWarning';
import { ResultListItem } from './components/ResultListItem';
import { useResultsNavigator } from './hooks/useResultsNavigator';
import { useTrackResultPage } from './hooks/useTrackResultPage';

interface ResultListProps {
    paxEnabled?: boolean;
}

export const ResultList = ({ paxEnabled }: ResultListProps) => {
    const { results, filters, currentPage, totalPages, createHref, handleChange, resetFilters, resetAndRefetch } = useResultsNavigator();
    const shouldShowPriceWarning = filters.agesOfChildren?.some((age) => age > 12 || age < 2);
    const [bannerPositions, bannerElements] = AdPositionElements.use({ layoutType: 'list' });

    const items = useMemo(
        () => results.offers.map((o) => <ResultListItem key={o.offerId} offer={o} filters={filters} paxEnabled={paxEnabled} />),
        [results.offers, filters, paxEnabled],
    );

    const itemsWithBanners = useMemo(
        () =>
            insertBanners({
                adItems: items,
                bannerPositions: bannerPositions as number[],
                bannerElements: bannerElements as ReactNode[],
            }),
        [items, bannerPositions, bannerElements],
    );

    useTrackResultPage(results, filters);

    return (
        <section
            data-testid="resultList"
            className="flex flex-col gap-16"
            aria-busy={results.status === ApiActionStatus.PENDING || undefined}>
            <h2 className="sr-only">Resultater</h2>
            {results.offers.length === 0 ? (
                <EmptyWarning id="result" resetFilters={resetFilters} triggerFetch={resetAndRefetch} />
            ) : (
                <>
                    {shouldShowPriceWarning && paxEnabled && (
                        <Box info>
                            <div className="flex space-x-16">
                                <IconInfo24 className="s-text-link mt-2" />
                                <p>{PRICE_MIGHT_VARY_WARNING}</p>
                            </div>
                        </Box>
                    )}
                    {itemsWithBanners}
                    {totalPages > 1 && (
                        <Pagination createHref={createHref} currentPage={currentPage} lastPage={totalPages} onChange={handleChange} />
                    )}
                </>
            )}
        </section>
    );
};
